import { FileWarning, Plus } from 'lucide-react';
import { useState, type ReactElement } from 'react';

import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { useToast } from '@/components/ui/toast';

import { GrantAccessModal } from './GrantAccessModal';
import type { GrantStatus, ProviderGrant } from '../types';
import { GRANT_STATUS_LABELS } from '../types';
import { useProviderGrants, useResendInvitation, useRevokeGrant } from '../use-provider-grants';
import { InlineError } from '@/components/ui/StatePanel';
import { formatInstantDate } from '@/lib/time';
import { skeletonLine } from '@/components/ui/skeleton';
import { errorAdvice } from '@/lib/error-advice';

const STATUS_BADGE: Record<GrantStatus, string> = {
  activo: 'b-ok',
  por_vencer: 'b-warn',
  vencido: 'b-mut',
  revocado: 'b-mut',
};

function scopeSummary(grant: ProviderGrant): string {
  const n = grant.scope?.grants.length ?? 0;
  if (n === 0) {
    return 'Sin alcance';
  }
  return n === 1 ? '1 recurso' : `${n} recursos`;
}

/** Accesos temporales de proveedor (escritura): listar, otorgar y revocar. Reusa
 *  ConfirmModal (antes de revocar) y toasts. El backend es el guardián real. */
export function ProviderGrants(): ReactElement {
  const grantsQuery = useProviderGrants({ enabled: true });
  const revoke = useRevokeGrant();
  const { showToast } = useToast();
  const [pending, setPending] = useState<{ id: string; name: string } | null>(null);
  const [grantOpen, setGrantOpen] = useState(false);
  const resend = useResendInvitation();

  function resendTo(userId: string, name: string): void {
    if (resend.isPending) {
      return;
    }
    resend.mutate(
      { userId },
      {
        onSuccess: (r) =>
          r === 'sent'
            ? showToast(`Invitación reenviada a ${name}`)
            : showToast(`No se pudo enviar el correo a ${name}. Intenta de nuevo.`, 'error'),
        onError: () =>
          showToast(`No se pudo reenviar la invitación a ${name}. Intenta de nuevo.`, 'error'),
      },
    );
  }

  function confirmRevoke(): void {
    if (!pending || revoke.isPending) {
      return;
    }
    revoke.mutate(
      { id: pending.id },
      {
        onSuccess: () => {
          showToast(`Acceso de ${pending.name} revocado`);
          setPending(null);
        },
        onError: () => {
          // Se deja el modal abierto para reintentar (no dead-end).
          showToast('No se pudo revocar el acceso. Intenta de nuevo.', 'error');
        },
      },
    );
  }

  const grants = grantsQuery.data ?? [];

  return (
    <div className="card rise" style={{ marginTop: 16 }}>
      <div className="ch">
        <h3>Accesos temporales de proveedor</h3>
        <button type="button" className="btn sm" onClick={() => setGrantOpen(true)}>
          <Plus aria-hidden="true" size={15} />
          Otorgar acceso
        </button>
      </div>

      {grantsQuery.isLoading ? (
        <div className="cb">
          <div className={skeletonLine({ width: 70 })} />
          <div className={skeletonLine({ width: 50, space: 'top' })} />
        </div>
      ) : grantsQuery.isError ? (
        <InlineError icon={<FileWarning />} onRetry={() => void grantsQuery.refetch()}>
          No se pudieron cargar los accesos. {errorAdvice(grantsQuery.error)}
        </InlineError>
      ) : grants.length === 0 ? (
        <div className="cb">
          <p className="soft" style={{ fontSize: 13, margin: 0 }}>
            No hay accesos temporales de proveedor en la empresa activa.
          </p>
        </div>
      ) : (
        <div className="t-scroll">
          <table className="t">
            <thead>
              <tr>
                <th>Proveedor</th>
                <th>Alcance</th>
                <th>Otorgó</th>
                <th>Vence</th>
                <th>Estado</th>
                <th aria-label="Acciones" />
              </tr>
            </thead>
            <tbody>
              {grants.map((g) => {
                const revocable = g.status === 'activo' || g.status === 'por_vencer';
                const name = g.provider.name ?? 'el proveedor';
                const resending =
                  resend.isPending && resend.variables?.userId === g.provider.userId;
                return (
                  <tr key={g.id}>
                    <td>
                      {g.provider.name ?? 'Sin asignar'}
                      {g.provider.external && (
                        <span className="badge b-mut" style={{ marginLeft: 6 }}>
                          Externo
                        </span>
                      )}
                    </td>
                    <td>{scopeSummary(g)}</td>
                    <td className="dim-mono" style={{ fontSize: 11 }}>
                      {g.grantedByName ?? '—'}
                    </td>
                    <td className="dim-mono" style={{ fontSize: 11 }}>
                      {formatInstantDate(g.expiresAt)}
                    </td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[g.status]}`}>
                        {GRANT_STATUS_LABELS[g.status]}
                      </span>
                    </td>
                    <td className="t-act">
                      <div className="row-actions">
                        {revocable && g.provider.external && (
                          <button
                            type="button"
                            className="btn mut sm"
                            disabled={resend.isPending}
                            onClick={() => resendTo(g.provider.userId, name)}
                          >
                            {resending ? 'Reenviando…' : 'Reenviar invitación'}
                          </button>
                        )}
                        {revocable && (
                          <button
                            type="button"
                            className="btn mut sm"
                            onClick={() => setPending({ id: g.id, name })}
                          >
                            Revocar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="soft" style={{ fontSize: 12, padding: '10px 16px 0', margin: 0 }}>
        El acceso se desactiva al vencer.
      </p>

      <ConfirmModal
        open={pending !== null}
        title="Revocar acceso"
        message={
          pending ? `¿Revocar el acceso de ${pending.name}? Perderá el acceso de inmediato.` : ''
        }
        confirmLabel="Revocar acceso"
        destructive
        busy={revoke.isPending}
        onConfirm={confirmRevoke}
        onCancel={() => setPending(null)}
      />
      <GrantAccessModal open={grantOpen} onClose={() => setGrantOpen(false)} />
    </div>
  );
}
